/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { Box, Typography } from "@mui/material";
import UPDATE_PASSWORD from "src/graphql/mutation/settings/updatePassword";
import KuikwitButton from "src/components/shared/atoms/button";
import { enqueueSnackbar } from "notistack";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import KuikTextField from "src/components/shared/atoms/textField/TextField";
import { safeJsonParse } from "src/utils/shared/safeJson";
import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useFormik } from "formik";
import * as Yup from "yup";
import { BackendErrorProps, PasswordProps } from "./Types";
import useStyles from "./Styles";

const ChangePassword = ({ meQueryResult }: { meQueryResult: any }) => {
  const { classes } = useStyles();

  const socialAuth = (() => {
    if (!meQueryResult?.me) return false;
    const { googleId, facebookId, instagramId } = meQueryResult.me;
    return Boolean(googleId || facebookId || instagramId);
  })();

  const [initialFormikValuesForPass] = useState({
    oldpass: "",
    newpass: "",
    conpass: "",
  });
  const { agentId } = useActiveUserDetail();

  const [
    updatePassword,
    {
      loading: updatePasswordMutationLoading,
      data: updatePasswordMutationResult,
    },
  ] = useMutation(UPDATE_PASSWORD);

  const [backendError, setBackendError] = useState<BackendErrorProps | null>(
    null,
  );

  useEffect(() => {
    if (updatePasswordMutationResult) {
      if (!updatePasswordMutationResult.updatePassword.success) {
        const backendErrors = safeJsonParse<BackendErrorProps>(
          updatePasswordMutationResult.updatePassword.error,
          {} as BackendErrorProps,
          "Update Password Error",
        );
        setBackendError(backendErrors);
      } else {
        enqueueSnackbar(updatePasswordMutationResult.updatePassword.error, {
          variant: "success",
        });
        setBackendError(null);
        handleClear();
      }
    }
  }, [updatePasswordMutationResult]);

  const passwordValidationSchema = Yup.object().shape({
    oldpass: socialAuth
      ? Yup.string().notRequired()
      : Yup.string()
          .min(3, "password must be at least 8 characters")
          .required("old password is required"),
    newpass: Yup.string()
      .min(8, "password must be at least 8 characters")
      .required("new password is required"),
    conpass: Yup.string()
      .oneOf([Yup.ref("newpass")], "password must match")
      .required("please confirm your password"),
  });

  const passwordHandleSubmit = async (
    values: PasswordProps,
    formikHelpers: any,
  ) => {
    try {
      formikHelpers.setSubmitting(false);
      await updatePassword({
        variables: {
          id: agentId,
          oldPassword: values.oldpass,
          password: values.newpass,
          confirmPassword: values.conpass,
        },
      });
    } catch (err: any) {
      enqueueSnackbar("Something went wrong! Try again.", { variant: "error" });
      formikHelpers.setSubmitting(false);
    }
  };

  const formikForChangePass = useFormik({
    initialValues: initialFormikValuesForPass,
    validationSchema: passwordValidationSchema,
    onSubmit: passwordHandleSubmit,
  });

  const handleClear = () => {
    formikForChangePass.resetForm();
  };

  return (
    <Box className={classes.detailsContainer}>
      {/* Hint for social auth users */}
      {socialAuth && (
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: "13px",
            color: "#999",
            backgroundColor: "rgba(0,0,0,0.03)",
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: "10px",
            padding: "10px 16px",
          }}
        >
          Your account is linked via a social provider. You can set a new
          password without entering a current one.
        </Typography>
      )}

      {/* Hidden password input to prevent autofill */}
      <input
        id="password"
        name="password"
        type="password"
        style={{ display: "none" }}
        autoComplete="new-password"
        readOnly
      />

      <Box className={classes.fieldsContainer}>
        {!socialAuth && (
          <Box className={classes.fieldBox}>
            <Typography className={classes.fieldLabel}>
              Current Password
            </Typography>
            <KuikTextField
              id="old"
              name="oldpass"
              typeField="password"
              value={formikForChangePass.values.oldpass}
              onChange={formikForChangePass.handleChange}
              helperText={
                (backendError && backendError.oldPassword) ||
                (formikForChangePass.touched.oldpass &&
                  formikForChangePass.errors.oldpass)
              }
              label="Enter your current password"
            />
          </Box>
        )}
        <Box className={classes.fieldBox}>
          <Typography className={classes.fieldLabel}>New Password</Typography>
          <KuikTextField
            id="newpass"
            typeField="password"
            name="newpass"
            value={formikForChangePass.values.newpass}
            onChange={formikForChangePass.handleChange}
            helperText={
              (backendError && backendError.newPassword) ||
              (formikForChangePass.touched.newpass &&
                formikForChangePass.errors.newpass)
            }
            label="Enter your new password"
          />
        </Box>
        <Box className={classes.fieldBox}>
          <Typography className={classes.fieldLabel}>
            Confirm Password
          </Typography>
          <KuikTextField
            id="conpass"
            typeField="password"
            name="conpass"
            value={formikForChangePass.values.conpass}
            onChange={formikForChangePass.handleChange}
            helperText={
              (backendError && backendError.confirmedPassword) ||
              (formikForChangePass.touched.conpass &&
                formikForChangePass.errors.conpass)
            }
            label="Re-enter your new password"
          />
        </Box>
      </Box>

      <Box>
        <KuikwitButton
          type="submit"
          handleClick={formikForChangePass.handleSubmit}
          variant="contained"
          className={classes.button}
          disabled={updatePasswordMutationLoading}
        >
          Update Password
        </KuikwitButton>
      </Box>
    </Box>
  );
};

export default ChangePassword;
